import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, ButtonBase, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { useDispatch, useSelector } from 'store';
import { activeID, activeItem, openDrawer } from 'store/slices/menu';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const Betastyle = {
    fontWeight: 700,
    color: 'red',
    fontSize: '0.83em',
    lineHeight: 1,
    ml: 0.35,
    verticalAlign: 'super',
    whiteSpace: 'nowrap'
};

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

/*
 * A nested row's left inset, in px, by depth.
 *
 * Three-level nesting is new to this sidebar — the admin tree reaches
 * Admin → qic → a team page — and the flat 24px step that suited two levels puts
 * a level-4 label 96px into a 260px drawer, where the longest admin labels
 * ("Assign Team Members", "Winner Shortlist") wrap. So the step eases off past
 * level 2: 12, 48, 60, 72.
 *
 * Exported because NavCollapse draws the guide line that runs down beside these
 * rows and has to land on the same x. One formula rather than two means the line
 * cannot drift off the rows it is drawn for.
 */
export const menuIndent = (level) => (level <= 1 ? 12 : level > 2 ? 48 + (level - 2) * 12 : level * 24);

const NavItem = ({ item, level, parentId }) => {
    const theme = useTheme();
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { pathname, search } = useLocation();
    const { layout, borderRadius } = useConfig();

    const { selectedItem, drawerOpen } = useSelector((state) => state.menu);
    const isSelected = selectedItem.findIndex((id) => id === item.id) > -1;

    const Icon = item?.icon;
    const itemIcon = item?.icon ? (
        <Icon
            stroke={1.5}
            size={drawerOpen ? '20px' : '24px'}
            // Design's `.glyph`: dimmed at rest, full strength when active.
            style={{
                color: isSelected ? theme.palette.secondary.main : theme.palette.text.primary,
                opacity: isSelected ? 1 : 0.8
            }}
        />
    ) : (
        <FiberManualRecordIcon
            sx={{
                color: isSelected ? theme.palette.secondary.main : theme.palette.text.primary,
                width: selectedItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: selectedItem.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />) };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch(activeItem([id]));
        if (matchesSM) dispatch(openDrawer(false));
        dispatch(activeID(parentId));
    };

    // active menu item on page load
    useEffect(() => {
        // The template matched this item's id against a pathname segment, which
        // is enough for every fixed tab because its id IS its path's last
        // segment. QIC's year rows cannot be told apart that way: all four share
        // the path /qic/dashboard and differ only in `?year=`, which a pathname
        // does not carry. So a url rule is ORed in beside the segment rule —
        // beside, not instead of, so pages carrying an unrelated query keep
        // lighting up exactly as they did.
        const onSegment = pathname.split('/').findIndex((id) => id === item.id) > -1;

        // The url rule has two halves. The whole url is what the page IS. The
        // query alone is there because the year row has to stay lit on the other
        // QIC pages too — /qic/dashboard/winners?year=2027 is the same year's
        // page, but its path is not the one the row names. That is the strip's
        // own rule: it keeps the year in the url wherever in the section you go.
        // Only the year rows carry a query, so this cannot over-match.
        const itemQuery = item?.url?.includes('?') ? item.url.slice(item.url.indexOf('?')) : '';
        const onUrl = Boolean(item?.url) && (item.url === `${pathname}${search}` || (itemQuery !== '' && itemQuery === search));

        if (onSegment || onUrl) {
            dispatch(activeItem([item.id]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, search]);

    const textColor = theme.palette.mode === 'dark' ? 'grey.400' : 'text.primary';
    const iconSelectedColor = theme.palette.mode === 'dark' && drawerOpen ? 'text.primary' : 'secondary.main';

    // The running championship, marked the way the in-page strip marks it: a
    // dot in `success.main` beside the label. Same token, not a literal green,
    // so the two markers cannot drift apart and no new colour enters the app.
    //
    // `inline-block` and a middle baseline because this sits in a Typography
    // rather than in the strip's flex row, where width and height would apply
    // to a bare inline span for free.
    const liveDot = item?.live && (
        <Box
            component="span"
            title="Current championship"
            data-testid="live-year"
            sx={{
                display: 'inline-block',
                verticalAlign: 'middle',
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                ml: '6px',
                bgcolor: theme.palette.success.main
            }}
        />
    );

    // The title cell: the label, the Beta marker a flagged item carries, and the
    // live dot. One expression, because both render branches below use it.
    const itemTitle = (
        <>
            {item?.isbeta==true ? (
                <>
                    {item.title}
                    <Box component="span" sx={Betastyle}>
                        (Beta)
                    </Box>
                </>
            ) : (
                item.title
            )}
            {liveDot}
        </>
    );

    return (
        <>
            {layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
                <ListItemButton
                    {...listItemProps}
                    disabled={item.disabled}
                    disableRipple={!drawerOpen}
                    sx={{
                        zIndex: 1201,
                        // Flat nav row per the "Simplified navigation" design:
                        // 12px padding all round, 9px radius, 3px vertical gap.
                        // Nested rows (level > 1) keep their indent.
                        borderRadius: '9px',
                        my: 0.375,
                        py: 1.5,
                        pl: drawerOpen ? (level > 1 ? `${menuIndent(level)}px` : 1.5) : 1.25,
                        ...(drawerOpen &&
                            level === 1 && {
                                // Neutral grey hover with a blue pill only when
                                // active — the design's #f4f7fc / --blue-soft
                                // pair. `action.hover` is mode-aware, and in
                                // dark mode the pill uses `action.selected`
                                // rather than secondary.light, because this
                                // app's dark `secondary` is PINK (#ec407a),
                                // not the design's blue.
                                '&:hover': {
                                    background: theme.palette.action.hover
                                },
                                '&.Mui-selected': {
                                    background:
                                        theme.palette.mode === 'dark' ? theme.palette.action.selected : theme.palette.secondary.light,
                                    color: iconSelectedColor,
                                    fontWeight: 600,
                                    '&:hover': {
                                        color: iconSelectedColor,
                                        background:
                                            theme.palette.mode === 'dark'
                                                ? theme.palette.action.selected
                                                : theme.palette.secondary.light
                                    }
                                }
                            }),
                        ...((!drawerOpen || level !== 1) && {
                            ...(level !== 1 && { py: 1 }),
                            '&:hover': {
                                bgcolor: 'transparent'
                            },
                            '&.Mui-selected': {
                                '&:hover': {
                                    bgcolor: 'transparent'
                                },
                                bgcolor: 'transparent'
                            }
                        })
                    }}
                    selected={isSelected}
                    onClick={() => itemHandler(item.id)}
                >
                    <ButtonBase sx={{ borderRadius: `${borderRadius}px` }} disableRipple={drawerOpen}>
                        <ListItemIcon
                            sx={{
                                // 20px glyph + the design's 12px gap at level 1.
                                // Nested rows carry a real glyph now rather than
                                // only the bullet, so they need room for it too
                                // — 18px was the bullet's width and would leave
                                // the label flush against the icon.
                                minWidth: !item?.icon ? 18 : level === 1 ? 32 : 28,
                                color: isSelected ? iconSelectedColor : textColor,
                                ...(!drawerOpen &&
                                    level === 1 && {
                                        borderRadius: `${borderRadius}px`,
                                        width: 46,
                                        height: 46,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '&:hover': {
                                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 25 : 'secondary.light'
                                        },
                                        ...(isSelected && {
                                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 25 : 'secondary.light',
                                            '&:hover': {
                                                bgcolor:
                                                    theme.palette.mode === 'dark' ? theme.palette.secondary.main + 30 : 'secondary.light'
                                            }
                                        })
                                    })
                            }}
                        >
                            {itemIcon}
                        </ListItemIcon>
                    </ButtonBase>

                    {(drawerOpen || (!drawerOpen && level !== 1)) && (
                        <ListItemText
                            primary={
                                <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit" component="div">
                                    {itemTitle}
                                </Typography>
                            }
                            secondary={
                                item.caption && (
                                    <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                                        {item.caption}
                                    </Typography>
                                )
                            }
                        />
                    )}

                    {drawerOpen && item.chip && (
                        <Chip
                            color={item.chip.color}
                            variant={item.chip.variant}
                            size={item.chip.size}
                            label={item.chip.label}
                            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                        />
                    )}
                </ListItemButton>
            ) : (
                <ListItemButton
                    {...listItemProps}
                    disabled={item.disabled}
                    sx={{
                        borderRadius: 0,
                        mb: 0.5,
                        alignItems: 'flex-start',
                        backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                        py: 1,
                        pl: 2
                    }}
                    selected={isSelected}
                    onClick={() => itemHandler(item.id)}
                >
                    <ListItemIcon
                        sx={{
                            my: 'auto',
                            minWidth: !item?.icon ? 18 : 36
                        }}
                    >
                        {itemIcon}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit" component="div">
                                {itemTitle}
                            </Typography>
                        }
                        secondary={
                            item.caption && (
                                <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                                    {item.caption}
                                </Typography>
                            )
                        }
                    />

                    {item.chip && (
                        <Chip
                            color={item.chip.color}
                            variant={item.chip.variant}
                            size={item.chip.size}
                            label={item.chip.label}
                            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                        />
                    )}
                </ListItemButton>
            )}
        </>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number,
    parentId: PropTypes.string
};

export default NavItem;
